import type { AbsChord, Chord, Pc, ScaleId } from '@astrasonora/annotation-schema'
import { labelChord } from './label.ts'

export interface Segment {
  start: number
  end: number
  abs: AbsChord
  /** The label carried an extension the parser reduced to the triad (6, 9, 11, 13, add). */
  reduced?: boolean
}

/** Merge consecutive segments with an identical absolute chord (spec §1 "chord unit"). */
export function mergeSegments(segments: readonly Segment[]): Segment[] {
  const out: Segment[] = []
  for (const s of segments) {
    const last = out[out.length - 1]
    if (last && sameAbs(last.abs, s.abs) && Math.abs(last.end - s.start) < 1e-6) {
      last.end = s.end
      if (s.reduced) last.reduced = true
    } else
      out.push({
        start: s.start,
        end: s.end,
        abs: { ...s.abs },
        ...(s.reduced ? { reduced: true } : {}),
      })
  }
  return out
}

function sameAbs(a: AbsChord, b: AbsChord): boolean {
  return (
    a.root === b.root && a.quality === b.quality && a.seventh === b.seventh && a.bass === b.bass
  )
}

export interface BeatGrid {
  barStarts: readonly number[]
  beatsPerBar: number
  bpm: number
}

/** Index of the bar containing time `t` (last bar start ≤ t). */
function barAt(t: number, barStarts: readonly number[]): number {
  let lo = 0
  let hi = barStarts.length - 1
  if (hi < 0) return 0
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1
    if (barStarts[mid]! <= t + 1e-6) lo = mid
    else hi = mid - 1
  }
  return lo
}

export interface AnnotateOptions {
  tonic: Pc
  /** The declared mode: labels are spelled from its tonic's major scale, the text against the mode (spec §3). */
  scale: ScaleId
  grid: BeatGrid
  /** Per merged segment, in order; defaults to all skip. `passage` splits the span before a quiz chord that follows one. */
  flags?: readonly { quiz: boolean; passage?: boolean | undefined }[]
}

/**
 * The chord list of a song from merged segments and the declared pair: labels computed by
 * `labelChord` with the following chord as resolution context; bar, beats and root position derived.
 */
export function annotate(segments: readonly Segment[], o: AnnotateOptions): Chord[] {
  const beatLen = 60 / o.grid.bpm
  return segments.map((s, i) => {
    const next = segments[i + 1]?.abs ?? null
    const f = o.flags?.[i] ?? { quiz: false }
    return {
      i,
      start: s.start,
      end: s.end,
      bar: barAt(s.start, o.grid.barStarts),
      beats: (s.end - s.start) / beatLen,
      abs: { ...s.abs },
      label: labelChord(s.abs, next, o.tonic, o.scale),
      quiz: f.quiz,
      // the break is kept only where it splits: on a quiz chord right after a quiz chord
      ...(f.quiz && f.passage && o.flags?.[i - 1]?.quiz ? { passage: true } : {}),
      rootPosition: s.abs.bass === s.abs.root,
      ...(s.reduced ? { reduced: true } : {}),
    }
  })
}

/**
 * The name a cleared span keeps across attempts (spec §7): where it starts and the chords it asks.
 * A curation change that touches the span changes its key; one elsewhere in the song does not.
 */
export function spanKey(chords: readonly Chord[], span: readonly [number, number]): string {
  const [a, b] = span
  const cells: string[] = []
  for (let i = a; i <= b; i++) cells.push(chords[i]!.label.cell)
  return `${chords[a]!.start.toFixed(1)}:${cells.join('>')}`
}

/** Every span key of a song, in song order. */
export function spanKeys(chords: readonly Chord[]): string[] {
  return quizSpans(chords).spans.map((s) => spanKey(chords, s))
}

/** The chord sounding at `t` seconds of song audio, or undefined between and beyond the chords. */
export function chordAt<C extends { start: number; end: number }>(
  chords: readonly C[],
  t: number,
): C | undefined {
  return chords.find((c) => t >= c.start && t < c.end)
}

/** Quiz chord indices and their maximal consecutive runs, split before a chord flagged `passage` (spec §7). */
export function quizSpans(chords: readonly Chord[]): { Q: number[]; spans: [number, number][] } {
  const Q = chords.filter((c) => c.quiz).map((c) => c.i)
  const spans: [number, number][] = []
  for (const i of Q) {
    const last = spans[spans.length - 1]
    if (last && last[1] === i - 1 && !chords[i]!.passage) last[1] = i
    else spans.push([i, i])
  }
  return { Q, spans }
}
